'use client';

import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2, Minimize2, Pencil, ScanSearch, XCircle } from 'lucide-react';
import type { FileCheckLevel, FileCheckResult, FileCheckTarget } from '@/lib/file-check/print-file-check';
import { fileCheckCopy, formatSizeCm } from '@/lib/file-check/print-file-check-copy';

const STYLE: Record<FileCheckLevel | 'checking', { card: string; icon: string; title: string }> = {
  green: { card: 'border-emerald-200 bg-emerald-50/70', icon: 'text-emerald-700', title: 'text-emerald-950' },
  orange: { card: 'border-amber-200 bg-amber-50/80', icon: 'text-amber-700', title: 'text-amber-950' },
  red: { card: 'border-rose-300 bg-rose-50', icon: 'text-rose-700', title: 'text-rose-950' },
  unchecked: { card: 'border-slate-200 bg-slate-50', icon: 'text-slate-600', title: 'text-slate-900' },
  checking: { card: 'border-blue-200 bg-blue-50/70', icon: 'text-blue-700', title: 'text-slate-900' },
};

const ICON = { green: CheckCircle2, orange: AlertTriangle, red: XCircle, unchecked: Info } as const;

export type PrintFileCheckCardProps = {
  locale: string;
  /** null: de check loopt nog. */
  result: FileCheckResult | null;
  target: FileCheckTarget | null;
  /** Extra regel bij `unchecked`, bijvoorbeeld "wij zetten je EPS om". */
  uncheckedNote?: string | null;
  onSelectSmaller?: (value: string) => void;
  onOpenEditor?: () => void;
  dpiCheckerHref?: string | null;
  onDpiCheckerClick?: () => void;
  /** Bestaande AI-knoppen (verbeteren, achtergrond weg) rechts in de kaart. */
  actions?: ReactNode;
};

/**
 * Stoplicht voor het bestand bij het gekozen formaat. Vaste opbouw (titel,
 * regels, knoppen) zodat de kaart niet verspringt als de uitslag binnenkomt.
 */
export function PrintFileCheckCard({ locale, result, target, uncheckedNote, onSelectSmaller, onOpenEditor, dpiCheckerHref, onDpiCheckerClick, actions }: PrintFileCheckCardProps) {
  const copy = fileCheckCopy(locale);
  const state = result ? result.level : 'checking';
  const style = STYLE[state];
  const Icon = state === 'checking' ? Loader2 : ICON[state];
  const sizeLabel = target ? formatSizeCm(target, locale) : '';
  const visibleIssues = result?.issues.filter((issue) => issue.code !== 'not_checked') || [];
  const fixableByEditor = Boolean(onOpenEditor) && visibleIssues.some((issue) => ['aspect_mismatch', 'orientation_mismatch', 'no_bleed', 'insufficient_bleed', 'size_mismatch'].includes(issue.code));
  const lowDpi = visibleIssues.some((issue) => issue.code === 'low_dpi' || issue.code === 'editor_low_resolution');

  return (
    <div className={`rounded-xl border p-4 transition-colors ${style.card}`} role="status" aria-live="polite" data-file-check={state}>
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{copy.heading}</p>
          <div className="mt-0.5 flex items-start gap-2">
            <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${style.icon} ${state === 'checking' ? 'animate-spin' : ''}`} aria-hidden="true" />
            <p className={`text-sm font-bold ${style.title}`}>{result ? copy.level[result.level] : copy.checking}</p>
          </div>
          {result && (
            <ul className="mt-2 space-y-1 text-xs leading-5 text-slate-700">
              {result.level === 'green' && result.dpi && target && !lowDpi && (
                <li>{copy.sharpAt(result.dpi, sizeLabel)}</li>
              )}
              {visibleIssues.map((issue, index) => (
                <li key={`${issue.code}-${index}`} className={issue.severity === 'red' ? 'font-medium text-rose-900' : issue.severity === 'orange' ? 'text-amber-950' : ''}>
                  {copy.issue[issue.code](issue, sizeLabel)}
                </li>
              ))}
              {result.level === 'unchecked' && <li>{uncheckedNote || copy.issue.not_checked({ code: 'not_checked', severity: 'tip' }, sizeLabel)}</li>}
            </ul>
          )}
          {result && (result.level === 'red' || result.level === 'orange') && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {result.smallerSize && onSelectSmaller && (
                <button
                  type="button"
                  onClick={() => onSelectSmaller(result.smallerSize!.value)}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                >
                  <Minimize2 className="h-4 w-4" aria-hidden="true" />
                  {copy.fixSmaller(result.smallerSize.label || formatSizeCm(result.smallerSize, locale), result.smallerSize.dpi)}
                </button>
              )}
              {fixableByEditor && (
                <button
                  type="button"
                  onClick={onOpenEditor}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-50"
                >
                  <Pencil className="h-4 w-4" aria-hidden="true" />
                  {copy.fixEditor}
                </button>
              )}
              {lowDpi && dpiCheckerHref && (
                <a
                  href={dpiCheckerHref}
                  target="_blank"
                  rel="noopener"
                  onClick={onDpiCheckerClick}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-blue-800 underline-offset-2 hover:underline"
                >
                  <ScanSearch className="h-4 w-4" aria-hidden="true" />
                  {copy.fixDpiChecker}
                </a>
              )}
            </div>
          )}
          {result?.smallerSize && onSelectSmaller && (result.level === 'red' || result.level === 'orange') && (
            <p className="mt-1 text-[11px] text-slate-500">{copy.fixSmallerNote}</p>
          )}
          {result && result.level !== 'green' && result.level !== 'unchecked' && (
            <p className="mt-2 text-[11px] text-slate-500">{copy.neverBlocks}</p>
          )}
        </div>
        {actions}
      </div>
    </div>
  );
}
